<div class="config-form-container">
	<form [formGroup]="configForm" *ngIf="!loading">
		<div class="row mb-3">
			<div class="col-md-12">
				<mat-slide-toggle formControlName="enabled">Enable 3PL Integration</mat-slide-toggle>
			</div>
		</div>

		<div class="row mb-3">
			<div class="col-md-6">
				<mat-form-field appearance="fill" class="w-100">
					<mat-label>Integration Type</mat-label>
					<mat-select formControlName="integration_type">
						<mat-option value="API">API</mat-option>
						<mat-option value="PGS">PGS</mat-option>
						<mat-option value="EDI">EDI</mat-option>
						<mat-option value="SFTP">SFTP</mat-option>
						<mat-option value="FTP">FTP</mat-option>
					</mat-select>
				</mat-form-field>
			</div>
			<div class="col-md-6">
				<mat-form-field appearance="fill" class="w-100">
					<mat-label>3PL Warehouse Code</mat-label>
					<input matInput formControlName="warehouse_code" required placeholder="e.g. CM, PICO, FW, NJ, 00">
					<mat-hint>3PL facility identifier (not an ERP location). Reorder reports use ERP locations only.</mat-hint>
				</mat-form-field>
			</div>
		</div>

		<div class="row mb-3">
			<div class="col-md-12">
				<mat-slide-toggle formControlName="test_mode">Test Mode</mat-slide-toggle>
			</div>
		</div>

		<!-- Order export -->
		<div formGroupName="config_json" class="config-section mb-3">
			<h5>Order export</h5>
			<p class="text-muted small mb-2">Order export rules. Status IDs from sales order (e.g. 1=Quote, 2=Order, 3=Invoiced). Comma-separated. Leave empty to use defaults.</p>
			<div class="row">
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Allowed statuses (export)</mat-label>
						<input matInput formControlName="allowed_statuses" placeholder="e.g. 2, 3">
						<mat-hint>Statuses that may be exported</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Blocked statuses</mat-label>
						<input matInput formControlName="blocked_statuses" placeholder="e.g. 1, 4">
						<mat-hint>Never export these</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Shipped statuses</mat-label>
						<input matInput formControlName="shipped_statuses" placeholder="e.g. 5">
						<mat-hint>Treated as already shipped</mat-hint>
					</mat-form-field>
				</div>
			</div>
		</div>

		<!-- Shipment & invoicing -->
		<div formGroupName="config_json" class="config-section mb-3">
			<h5>Shipment &amp; invoicing</h5>
			<p class="text-muted small mb-2">When shipments are imported from 3PL: order status is updated first. If auto-invoice is on, invoice is created after; <code>qtyinvoiced</code> is only updated by the invoice flow.</p>
			<div class="row mb-2">
				<div class="col-md-12">
					<mat-slide-toggle formControlName="auto_invoice_on_shipment">Auto-invoice on shipment</mat-slide-toggle>
					<mat-hint class="d-block">When enabled, create invoice automatically after importing a shipment (uses same path as orders/invoice).</mat-hint>
				</div>
			</div>
			<div class="row">
				<div class="col-md-6 col-lg-3">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when shipment received</mat-label>
						<mat-select formControlName="status_on_shipment_received">
							<mat-option [value]="">— Use shipped statuses</mat-option>
							<mat-option *ngFor="let s of orderStatuses" [value]="s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
					</mat-form-field>
				</div>
				<div class="col-md-6 col-lg-3">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when in transit</mat-label>
						<mat-select formControlName="status_on_tracking_in_transit">
							<mat-option [value]="">—</mat-option>
							<mat-option *ngFor="let s of orderStatuses" [value]="s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
					</mat-form-field>
				</div>
				<div class="col-md-6 col-lg-3">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when delivered</mat-label>
						<mat-select formControlName="status_on_tracking_delivered">
							<mat-option [value]="">—</mat-option>
							<mat-option *ngFor="let s of orderStatuses" [value]="s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
					</mat-form-field>
				</div>
				<div class="col-md-6 col-lg-3">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Status when invoice failed</mat-label>
						<mat-select formControlName="status_on_invoice_failed">
							<mat-option [value]="">—</mat-option>
							<mat-option *ngFor="let s of orderStatuses" [value]="s.order_status_id">{{ s.name }} ({{ s.order_status_id }})</mat-option>
						</mat-select>
						<mat-hint>e.g. Needs Invoiced (91)</mat-hint>
					</mat-form-field>
				</div>
			</div>
		</div>

		<!-- Products to push to 3PL -->
		<div *ngIf="integrationType === 'PGS'" formGroupName="config_json" class="config-section mb-3">
			<h5>Products to push to 3PL</h5>
			<p class="text-muted small mb-2">Which products to send when you use the Product master tab. Leave all empty to push all non-discontinued items.</p>
			<div class="row">
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Categories to push</mat-label>
						<mat-select formControlName="sku_categories" multiple placeholder="All categories">
							<mat-option *ngFor="let c of categories" [value]="c.categoryid">{{ c.categorydescription }}{{ c.categoryid ? ' (' + c.categoryid + ')' : '' }}</mat-option>
						</mat-select>
						<mat-hint>Leave empty to push all categories</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Product lines to push</mat-label>
						<mat-select formControlName="sku_lines" multiple placeholder="All product lines">
							<mat-option *ngFor="let p of productlines" [value]="p.line_field">{{ p.line_description }}{{ p.line_field ? ' (' + p.line_field + ')' : '' }}</mat-option>
						</mat-select>
						<mat-hint>Leave empty to push all product lines</mat-hint>
					</mat-form-field>
				</div>
				<div class="col-md-4">
					<mat-form-field appearance="fill" class="w-100">
						<mat-label>Brands to push</mat-label>
						<mat-select formControlName="sku_manufacturers" multiple placeholder="All brands" [compareWith]="compareByManufacturerId">
							<mat-option *ngFor="let b of brands" [value]="b.manufacturer_id">{{ b.name }}{{ b.mfg_code ? ' (' + b.mfg_code + ')' : '' }}</mat-option>
						</mat-select>
						<mat-hint>Leave empty to push all brands</mat-hint>
					</mat-form-field>
				</div>
			</div>
			<div *ngIf="(categories?.length === 0 || productlines?.length === 0 || brands?.length === 0) && !loading" class="text-muted small mt-1">
				<span *ngIf="categories?.length === 0">No categories found.</span>
				<span *ngIf="productlines?.length === 0">No product lines found.</span>
				<span *ngIf="brands?.length === 0">No brands found.</span>
			</div>
		</div>

		<!-- API Configuration -->
		<div *ngIf="integrationType === 'API'" formGroupName="config_json">
			<div formGroupName="api" class="config-section">
				<h5>API Configuration</h5>
				<div class="row">
					<div class="col-md-12">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Base URL</mat-label>
							<input matInput formControlName="baseUrl" placeholder="https://api.3plprovider.com">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>API Key</mat-label>
							<input matInput type="password" formControlName="apiKey">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>API Secret</mat-label>
							<input matInput type="password" formControlName="apiSecret">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Timeout (seconds)</mat-label>
							<input matInput type="number" formControlName="timeout">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Retry Attempts</mat-label>
							<input matInput type="number" formControlName="retryAttempts">
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<!-- PGS Configuration -->
		<div *ngIf="integrationType === 'PGS'" formGroupName="config_json">
			<div formGroupName="pgs" class="config-section">
				<h5>PGS Configuration</h5>
				<div class="row">
					<div class="col-md-12">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Base URL</mat-label>
							<input matInput formControlName="baseUrl" placeholder="https://apiqa.pgs360.com/CustomerIntegrator">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>User ID</mat-label>
							<input matInput formControlName="userId">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Password</mat-label>
							<input matInput type="password" formControlName="passwd">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Default Warehouse</mat-label>
							<mat-select formControlName="defaultWarehouse">
								<mat-option value="CM">CM</mat-option>
								<mat-option value="PICO">PICO</mat-option>
								<mat-option value="FW">FW</mat-option>
								<mat-option value="NJ">NJ</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Default Outbound Channel</mat-label>
							<mat-select formControlName="defaultOutboundChannel">
								<mat-option value="B2B">B2B</mat-option>
								<mat-option value="B2C">B2C</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-4">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>B2C Service Type</mat-label>
							<input matInput formControlName="b2cServiceType" placeholder="Ground">
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<!-- EDI Configuration -->
		<div *ngIf="integrationType === 'EDI'" formGroupName="config_json">
			<div formGroupName="edi" class="config-section">
				<h5>EDI Configuration</h5>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Trading Partner ID</mat-label>
							<input matInput formControlName="tradingPartnerId">
						</mat-form-field>
					</div>
					<div class="col-md-3">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>ISA Qualifier</mat-label>
							<input matInput formControlName="isaQualifier">
						</mat-form-field>
					</div>
					<div class="col-md-3">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>GS Qualifier</mat-label>
							<input matInput formControlName="gsQualifier">
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<!-- SFTP/FTP Configuration -->
		<div *ngIf="integrationType === 'SFTP' || integrationType === 'FTP'" formGroupName="config_json">
			<div formGroupName="sftp" class="config-section">
				<h5>{{ integrationType }} Configuration</h5>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Host</mat-label>
							<input matInput formControlName="host" placeholder="sftp.3plprovider.com">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Port</mat-label>
							<input matInput type="number" formControlName="port">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Username</mat-label>
							<input matInput formControlName="username">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Password</mat-label>
							<input matInput type="password" formControlName="password">
						</mat-form-field>
					</div>
				</div>
				<div class="row">
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Remote Path (Inbound)</mat-label>
							<input matInput formControlName="remotePath">
						</mat-form-field>
					</div>
					<div class="col-md-6">
						<mat-form-field appearance="fill" class="w-100">
							<mat-label>Outbound Path</mat-label>
							<input matInput formControlName="outboundPath">
						</mat-form-field>
					</div>
				</div>
			</div>
		</div>

		<div class="row mt-4">
			<div class="col-md-12">
				<button mat-raised-button color="primary" (click)="saveConfig()" [disabled]="saving || configForm.invalid">
					<mat-spinner *ngIf="saving" diameter="18" class="d-inline-block mr-1"></mat-spinner>
					<span *ngIf="!saving">Save Configuration</span>
					<span *ngIf="saving">Saving...</span>
				</button>
				<button mat-raised-button color="accent" (click)="testConnection()" [disabled]="testing" class="ml-2">
					<mat-spinner *ngIf="testing" diameter="18" class="d-inline-block mr-1"></mat-spinner>
					<span *ngIf="!testing">Test Connection</span>
					<span *ngIf="testing">Testing...</span>
				</button>
			</div>
		</div>

		<div *ngIf="testResult" class="mt-3">
			<div [ngClass]="{'alert-success': testResult.success, 'alert-danger': !testResult.success}" class="alert">
				<strong>{{ testResult.success ? 'Success' : 'Failed' }}:</strong> {{ testResult.message }}
			</div>
			<div *ngIf="!testResult.success && testResult.response" class="mt-2">
				<details>
					<summary>API response</summary>
					<pre class="mb-0 p-2 bg-light small config-test-response-pre">{{ testResult.response | json }}</pre>
				</details>
			</div>
		</div>
	</form>

	<div *ngIf="loading" class="text-center p-4">
		<mat-spinner diameter="50"></mat-spinner>
		<p>Loading configuration...</p>
	</div>
</div>
